import { NodeDimensionChange, useReactFlow, useStoreApi } from '@xyflow/react';
import { useCallback, useMemo } from 'react';

import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';

import { Node, TileNode } from '../types';
import { isTileNode } from '../utils/nodes';

/**
 * Resolves a tile's display type. A tile is an "Icon" only when its
 * componentType is explicitly TileIcon; everything else (including an unset
 * componentType) renders as a standard "Tile".
 */
function getNodeDisplayType(node: TileNode): GraphVizNodeComponentType {
  return node.data.componentType === GraphVizNodeComponentType.TileIcon
    ? GraphVizNodeComponentType.TileIcon
    : GraphVizNodeComponentType.Tile;
}

/**
 * Shared hook for reading and changing the display type (Tile vs Icon) of the
 * selected tiles in bulk. Used by the EntityMap (ownership) and EntityDiagram
 * (single entity) edit trowsers. Uses `updateNode` for immediate-mode updates
 * that flow through each diagram's `onNodesChange` → reducer path.
 *
 * The estate waterfall has its own version at
 * `src/modules/estateWaterfall/hooks/useSelectedComponentType.ts` that
 * dispatches UPDATE_TILE_CONFIG through its reducer for undo/redo support.
 *
 * Switching between Tile and Icon changes the rendered layout height, so this
 * hook resets height and re-measures — mirroring `useSelectedIconShape`.
 */
export function useSelectedComponentType(nodes: Node[]) {
  const { updateNode, getNode } = useReactFlow();
  const storeApi = useStoreApi();

  const selectedTiles = useMemo(
    () => nodes.filter(isTileNode).filter((n) => !!n.selected),
    [nodes]
  );

  const hasConvertibleTilesSelected = selectedTiles.length > 0;

  const { currentType, isMixed } = useMemo((): {
    currentType: GraphVizNodeComponentType;
    isMixed: boolean;
  } => {
    const [firstTile] = selectedTiles;
    if (!firstTile)
      return { currentType: GraphVizNodeComponentType.Tile, isMixed: false };

    const firstType = getNodeDisplayType(firstTile);
    const allSame = selectedTiles.every(
      (n) => getNodeDisplayType(n) === firstType
    );

    return allSame
      ? { currentType: firstType, isMixed: false }
      : { currentType: GraphVizNodeComponentType.Tile, isMixed: true };
  }, [selectedTiles]);

  const setComponentType = useCallback(
    (componentType: GraphVizNodeComponentType) => {
      for (const tile of selectedTiles) {
        // Skip tiles already at the target type so we don't trigger a pointless
        // height reset / re-measure (which would flicker the tile).
        if (getNodeDisplayType(tile) === componentType) continue;

        const n = getNode(tile.id);
        if (!n) continue;

        // Merge componentType into data AND clear height in a single updateNode
        // call so the change flows through onNodesChange → reducer. A separate
        // updateNodeData call would only update React Flow's internal store
        // without triggering onNodesChange, causing the controlled nodes prop
        // to overwrite the change on re-render.
        updateNode(tile.id, {
          width: n.width,
          height: undefined,
          measured: {
            width: n.measured?.width,
            height: undefined,
          },
          data: { ...n.data, componentType },
        });

        // Auto-width tiles (entity diagram) drive their own width via
        // data.width's min/max object; pinning node.width with setAttributes
        // fights that measurement. Their save paths don't persist node.height
        // either, so skip the re-measure dance entirely.
        const dataWidth = tile.data.width;
        if (typeof dataWidth === 'object' && dataWidth.width === 'auto') {
          continue;
        }

        // After ReactFlow re-measures the tile at its new natural height,
        // dispatch a dimensions change with setAttributes: true so node.height
        // (not just node.measured.height) is written to the new value. Without
        // this, node.height stays undefined and save paths that persist height
        // (e.g. entity map) fall back to the builder default on reload, causing
        // clipped content. Two rAFs: first lets React render with undefined
        // height, second lets ReactFlow's ResizeObserver measure.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const updated = getNode(tile.id);
            if (!updated) return;

            const measuredHeight = updated.measured?.height ?? updated.height;
            const measuredWidth = updated.measured?.width ?? updated.width;
            if (measuredHeight === undefined || measuredWidth === undefined) {
              return;
            }

            const dimensionChange: NodeDimensionChange = {
              id: tile.id,
              type: 'dimensions',
              dimensions: { width: measuredWidth, height: measuredHeight },
              setAttributes: true,
            };

            storeApi.getState().triggerNodeChanges([dimensionChange]);
          });
        });
      }
    },
    [selectedTiles, updateNode, getNode, storeApi]
  );

  return {
    hasConvertibleTilesSelected,
    currentType,
    isMixed,
    setComponentType,
  };
}
